import React from 'react';
import { useSemester, useSemesterProgress } from '../../hooks';
import { formatDate } from '../../utils/dateUtils';

/**
 * CurrentSemesterBadge - Displays current semester information
 * @param {Object} props
 * @param {string|Date} props.classStartDate - The start date of the class
 * @param {boolean} props.showProgress - Whether to show progress bar
 * @param {string} props.size - Size variant: 'sm', 'md', 'lg'
 */
const CurrentSemesterBadge = ({ classStartDate, professionType, showProgress = true, size = 'md' }) => {
  const { currentSemester, hasActiveSemester } = useSemester(classStartDate, professionType);
  const { progress } = useSemesterProgress(
    currentSemester?.startDate,
    currentSemester?.endDate
  );

  if (!currentSemester) {
    return (
      <div className={`inline-flex items-center px-3 py-1 rounded-full bg-gray-100 text-gray-600 ${
        size === 'sm' ? 'text-xs' : size === 'lg' ? 'text-base' : 'text-sm'
      }`}>
        <i className="fas fa-info-circle mr-2"></i>
        No active semester
      </div>
    );
  }

  const sizeClasses = {
    sm: 'text-xs px-2 py-1',
    md: 'text-sm px-3 py-2',
    lg: 'text-base px-4 py-3',
  };

  const iconSizes = {
    sm: 'text-xs',
    md: 'text-sm',
    lg: 'text-base',
  };

  const statusColor = hasActiveSemester 
    ? 'bg-pale-dawn text-pale-dawn/800 border-pale-dawn/80' 
    : 'bg-orange-100 text-orange-800 border-orange-300';

  const semesterType = currentSemester.isOdd ? 'Fall' : 'Spring';
  const semesterLabel = `Semester ${currentSemester.semesterNumber}`;

  return (
    <div className={`inline-flex flex-col ${sizeClasses[size]} rounded-lg border ${statusColor}`}>
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <i className={`fas fa-calendar-alt ${iconSizes[size]}`}></i>
          <div>
            <div className="font-bold">
              {semesterLabel} - {semesterType} {currentSemester.academicYear}
            </div>
            {hasActiveSemester ? (
              <div className={`${size === 'sm' ? 'text-xs' : 'text-xs'} opacity-80 mt-0.5`}>
                {currentSemester.daysUntilEnd} days remaining
              </div>
            ) : (
              <div className={`${size === 'sm' ? 'text-xs' : 'text-xs'} opacity-80 mt-0.5`}>
                {currentSemester.nextSemesterStartsIn 
                  ? `Next semester in ${currentSemester.nextSemesterStartsIn} days`
                  : 'Semester ended'}
              </div>
            )}
          </div>
        </div>
        
        {hasActiveSemester && (
          <div className="flex items-center gap-2">
            <span className="font-semibold">{progress}%</span>
          </div>
        )}
      </div>

      {showProgress && hasActiveSemester && (
        <div className="mt-2 w-full bg-white/50 rounded-full h-2 overflow-hidden">
          <div
            className="h-full bg-grey-dusk transition-all duration-500"
            style={{ width: `${progress}%` }}
          ></div>
        </div>
      )}

      {size !== 'sm' && (
        <div className={`mt-2 pt-2 border-t border-current/20 ${size === 'lg' ? 'text-sm' : 'text-xs'} opacity-75`}>
          <div className="flex justify-between">
            <span>Start: {formatDate(currentSemester.startDate, { month: 'short', day: 'numeric' })}</span>
            <span>End: {formatDate(currentSemester.endDate, { month: 'short', day: 'numeric' })}</span>
          </div>
        </div>
      )}
    </div>
  );
};

/**
 * SemesterTimeline - Displays a timeline of all semesters
 * @param {Object} props
 * @param {string|Date} props.classStartDate - The start date of the class
 * @param {number|string} props.professionType - Profession type or total semesters
 */
export const SemesterTimeline = ({ classStartDate, professionType = 6 }) => {
  const { allSemesters, currentSemester, getSemesterStatus } = useSemester(
    classStartDate,
    professionType
  );

  if (allSemesters.length === 0) {
    return null;
  }

  const getStatusColor = (status) => {
    switch (status) {
      case 'active':
        return 'bg-green-cloud border-green-cloud/80';
      case 'completed':
        return 'bg-sunrise-red border-sunrise-red/80';
      case 'upcoming':
        return 'bg-gray-300 border-gray-400';
      default:
        return 'bg-gray-200 border-gray-300';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'active':
        return 'fa-play-circle';
      case 'completed':
        return 'fa-check-circle';
      case 'upcoming':
        return 'fa-clock';
      default:
        return 'fa-circle';
    }
  };

  return (
    <div className="w-full">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-bold text-gray-800">
          <i className="fas fa-timeline mr-2"></i>
          Semester Timeline
        </h3>
        {currentSemester && (
          <span className="text-sm text-gray-600">
            Current: Semester {currentSemester.semesterNumber}
          </span>
        )}
      </div>

      <div className="relative">
        {/* Timeline line */}
        <div className="absolute top-6 left-0 right-0 h-1 bg-gray-200"></div>

        {/* Semester markers */}
        <div className="relative flex justify-between">
          {allSemesters.map((semester) => {
            const status = getSemesterStatus(semester.semesterNumber);
            const isActive = status === 'active';

            return (
              <div
                key={semester.semesterNumber}
                className="flex flex-col items-center"
                style={{ width: `${100 / allSemesters.length}%` }}
              >
                {/* Marker circle */}
                <div
                  className={`relative z-10 w-12 h-12 rounded-full border-4 flex items-center justify-center transition-all ${getStatusColor(
                    status
                  )} ${isActive ? 'scale-110 shadow-lg' : ''}`}
                >
                  <i className={`fas ${getStatusIcon(status)} text-white text-lg`}></i>
                </div>

                {/* Semester info */}
                <div className="mt-3 text-center">
                  <div
                    className={`font-bold ${
                      isActive ? 'text-green-cloud' : 'text-gray-700'
                    }`}
                  >
                    S{semester.semesterNumber}
                  </div>
                  <div className="text-xs text-gray-500 mt-1">
                    {semester.isOdd ? 'Fall' : 'Spring'}
                  </div>
                  <div className="text-xs text-gray-400">
                    {semester.academicYear}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default CurrentSemesterBadge;
